/**
 * Zustand store for agent state in canvas workspace.
 *
 * Manages project agents (for InputBar/sidebar), and caches
 * the skill registry (for settings editor).
 */

import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { useProjectStore } from "@/stores/project-store";
import type {
  AgentTemplate,
  ProjectAgent,
  Skill,
} from "@/api/agents";
import * as agentsApi from "@/api/agents";

interface AgentState {
  specialistCreationEnabled: boolean;
  setSpecialistCreationEnabled: (enabled: boolean) => void;
  // Canvas workspace agents
  agents: ProjectAgent[];
  agentsProjectId: string | null;
  selectedAgentId: string | null;

  // All user templates (for hire popup)
  templates: AgentTemplate[];

  // Registry cache (for settings editor)
  skills: Skill[];

  isLoading: boolean;
  error: string | null;

  fetchAllTemplates: () => Promise<void>;
  fetchProjectAgents: (projectId: string) => Promise<void>;
  addAgentToProject: (projectId: string, templateId: string) => Promise<void>;
  createAndHireAgent: (projectId: string, name: string) => Promise<void>;
  removeAgentFromProject: (
    projectId: string,
    agentId: string,
  ) => Promise<void>;
  fetchSkills: () => Promise<void>;
  selectAgent: (id: string | null) => void;
  clear: () => void;
}

const SELECTED_AGENT_KEY = "denker:selectedAgentId";
let latestAgentRosterRequest = 0;

function isSelectedProject(projectId: string): boolean {
  const selectedId = useProjectStore.getState().currentProject?.id;
  return selectedId == null || selectedId === projectId;
}

/** Sort agents: builtin (Denker) first, then alphabetically by name. */
function sortAgents<T extends { name?: string; template_name?: string; is_builtin?: boolean; is_template_builtin?: boolean }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const aBuiltin = a.is_builtin ?? a.is_template_builtin ?? false;
    const bBuiltin = b.is_builtin ?? b.is_template_builtin ?? false;
    if (aBuiltin !== bBuiltin) return aBuiltin ? -1 : 1;
    const aName = (a.name ?? a.template_name ?? "").toLowerCase();
    const bName = (b.name ?? b.template_name ?? "").toLowerCase();
    return aName.localeCompare(bName);
  });
}

export const useAgentStore = create<AgentState>()(mirroredStore("agent", (set, get) => ({
  specialistCreationEnabled: false,
  setSpecialistCreationEnabled: (enabled) => set({ specialistCreationEnabled: enabled }),
  agents: [],
  agentsProjectId: null,
  selectedAgentId: localStorage.getItem(SELECTED_AGENT_KEY) || null,
  templates: [],
  skills: [],
  isLoading: false,
  error: null,

  fetchAllTemplates: async () => {
    const isCurrentAuthScope = captureDesktopChatAuthScopeGuard();
    try {
      const data = await agentsApi.listAgentTemplates(false, (enabled) => {
        if (isCurrentAuthScope()) get().setSpecialistCreationEnabled(enabled);
      });
      if (!isCurrentAuthScope()) return;
      set({ templates: sortAgents(data) });
    } catch (error) {
      console.error("Failed to fetch agent templates:", error);
    }
  },

  fetchProjectAgents: async (projectId) => {
    if (!isSelectedProject(projectId)) return;
    const isCurrentAuthScope = captureDesktopChatAuthScopeGuard();
    const requestId = ++latestAgentRosterRequest;
    const changedProject = get().agentsProjectId !== projectId;
    set({
      isLoading: true,
      error: null,
      ...(changedProject ? { agents: [], agentsProjectId: null } : {}),
    });
    try {
      const agents = sortAgents(await agentsApi.listProjectAgents(projectId, (enabled) => {
        if (isCurrentAuthScope() && isSelectedProject(projectId) && requestId === latestAgentRosterRequest) {
          get().setSpecialistCreationEnabled(enabled);
        }
      }));
      if (!isCurrentAuthScope() || !isSelectedProject(projectId) || requestId !== latestAgentRosterRequest) return;
      set({ agents, agentsProjectId: projectId, isLoading: false });

      // Auto-select: restore saved selection if valid, else pick builtin (Denker), else first
      const current = get().selectedAgentId;
      const stillValid = current && agents.some((a) => a.id === current
        && (get().specialistCreationEnabled || a.is_template_builtin));
      if (!stillValid && agents.length > 0) {
        const builtin = agents.find((a) => a.is_template_builtin);
        const pick = builtin ?? agents[0];
        if (pick) {
          get().selectAgent(pick.id);
        }
      }
    } catch {
      if (!isCurrentAuthScope() || !isSelectedProject(projectId) || requestId !== latestAgentRosterRequest) return;
      set({ error: "Failed to load agents", isLoading: false });
    }
  },

  addAgentToProject: async (projectId, templateId) => {
    try {
      await agentsApi.assignAgentToProject(projectId, templateId);
      await get().fetchProjectAgents(projectId);
    } catch {
      if (isSelectedProject(projectId)) set({ error: "Failed to add agent" });
    }
  },

  createAndHireAgent: async (projectId, name) => {
    try {
      const template = await agentsApi.createAgentTemplate({ name });
      await agentsApi.assignAgentToProject(projectId, template.id);
      await get().fetchProjectAgents(projectId);
      await get().fetchAllTemplates();
    } catch {
      if (isSelectedProject(projectId)) set({ error: "Failed to create agent" });
    }
  },

  removeAgentFromProject: async (projectId, agentId) => {
    try {
      await agentsApi.removeAgentFromProject(projectId, agentId);
      if (isSelectedProject(projectId) && get().selectedAgentId === agentId) {
        set({ selectedAgentId: null });
      }
      await get().fetchProjectAgents(projectId);
    } catch {
      if (isSelectedProject(projectId)) set({ error: "Failed to remove agent" });
    }
  },

  fetchSkills: async () => {
    const isCurrentAuthScope = captureDesktopChatAuthScopeGuard();
    try {
      const skills = await agentsApi.listSkills();
      if (!isCurrentAuthScope()) return;
      set({ skills });
    } catch {
      // Skills are non-critical, don't block UI
    }
  },

  selectAgent: (id) => {
    if (id) {
      localStorage.setItem(SELECTED_AGENT_KEY, id);
    } else {
      localStorage.removeItem(SELECTED_AGENT_KEY);
    }
    set({ selectedAgentId: id });
  },

  clear: () => {
    latestAgentRosterRequest += 1;
    localStorage.removeItem(SELECTED_AGENT_KEY);
    set({
      specialistCreationEnabled: false,
      agents: [],
      agentsProjectId: null,
      selectedAgentId: null,
      templates: [],
      skills: [],
      isLoading: false,
      error: null,
    });
  },
})));

/**
 * How long a caller will wait for the roster to be repaired.
 *
 * Bounded because `apiRequest` is not: it has no timeout of its own, so a
 * server that accepts the connection and never answers would hold this promise
 * for the life of the window. Waiting forever to find out whether we may speak
 * is a worse failure than the one being fixed - it would be silent, and it
 * would take every later prompt down with it.
 *
 * The fetch is not cancelled when the wait expires. It carries on and repairs
 * the window for the next attempt, which is what the synchronous caller in
 * `dispatchUserMessage` relies on.
 */
const ROSTER_REPAIR_TIMEOUT_MS = 2_000;

/** What a repair attempt found, so the caller can say which it was. */
export type RosterRepair = "loaded" | "empty" | "unavailable";

/**
 * Make sure this window knows which agents the project has, before deciding it
 * has none.
 *
 * The roster is fetched once, when a window opens, and only by the owner
 * window. Nothing refetches it if that fetch failed, if the window was rebuilt
 * after a display change, or if a mirror never received the owner's snapshot.
 * An empty roster is therefore far more often a window that never loaded one
 * than a project that truly has no agents - and the cost of getting that wrong
 * was telling somebody to add an agent they already had, and discarding what
 * they had just said.
 *
 * `unavailable` and `empty` are kept apart because only one of them is the
 * user's to fix, and the store cannot be asked afterwards: `fetchProjectAgents`
 * clears `error` synchronously at its start, so a caller that reads the store
 * after starting a refetch always sees `null`.
 */
export async function ensureAgentRoster(
  projectId: string,
  timeoutMs = ROSTER_REPAIR_TIMEOUT_MS,
): Promise<RosterRepair> {
  const current = useAgentStore.getState();
  if (current.agentsProjectId === projectId && current.agents.length > 0) return "loaded";

  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = Symbol("roster-repair-timeout");
  const deadline = new Promise<typeof expired>((resolve) => {
    timer = setTimeout(() => resolve(expired), timeoutMs);
  });
  try {
    const raced = await Promise.race([
      useAgentStore.getState().fetchProjectAgents(projectId),
      deadline,
    ]);
    if (raced === expired) return "unavailable";
  } finally {
    clearTimeout(timer);
  }

  const refreshed = useAgentStore.getState();
  if (refreshed.agentsProjectId === projectId && refreshed.agents.length > 0) return "loaded";
  // `fetchProjectAgents` swallows its own failure and records it here, so this
  // is the only place the difference survives.
  return useAgentStore.getState().error ? "unavailable" : "empty";
}
